import React, { Fragment, ReactNode, useState } from 'react';
import OutageLeftPanel from './components/common/outage-left-panel/OutageLeftPanel';
import MobileSubHeader from './components/mobile-sub-header/mobile-sub-header';
import { OutageCenterMap } from './components/outage-center-map';
import {
  OutageLeftPanelStateProvider,
  useOutageLeftPanelState,
} from './providers/OutageLeftPanelStateProvider';
import {
  OutageMapOptionsProvider,
  useOutageMapOptions,
} from './providers/OutageMapOptionsProvider';
import { OutageResponsivePageTrackerProvider } from './providers/OutageResponsivePageTracker';
import OutageHeaderSearchBar from './components/OutageSearchBar/OutageSearchBar';
import {
  Button,
  createStyles,
  Divider,
  Grid,
  makeStyles,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { useIsMobile } from '../../util/style-utils';
import MultiPremisePopup from './components/MultiPremisePopup/MultiPremisePopup';
import { OutageAccountListProvider } from './providers/OutageAccountListProvider';
import { OutageMapProvider } from './providers/OutageMapProvider';
import AlertIcon from '../../static/Icon-Alert.svg';
import { useTranslation } from '../../hooks/useTranslation';
import { useOutageEventsDetails } from './hooks/useOutageEventsDetails';
import { ReportOutageByCountyCity } from './components/report-outage-by-county-city';
import { OutageMapDialog } from './components/outage-map-dialog';
import MapOptions from '../../static/MapOptions.svg';
import MapOptionsSelected from '../../static/MapOptions_Selected.svg';
import colors from '../../themes/main-colors';

type OutagesPageProps = {
  path?: string;
  children?: ReactNode;
};

const useStyles = makeStyles<Theme, { isMobile: boolean; panelOpen: boolean }>(
  (theme: Theme) =>
    createStyles({
      headerStyles: {
        position: 'absolute',
        left: '38%',
        top: ({ isMobile }) => (isMobile ? 140 : 12),
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        maxWidth: 960,
      },
      buttonContainer: {
        opacity: 0.85,
        width: 'max-content',
        position: 'fixed',
        bottom: '3%',
        zIndex: 999,
        display: 'flex',
        boxShadow: theme.shadows[5],
        borderRadius: 30,
        overflow: 'hidden',
        background: colors.white,
        padding: '0.5rem',
        justifySelf: 'center',
        marginLeft: ({ panelOpen, isMobile }) =>
          isMobile ? '' : panelOpen ? '340px' : '80px',
      },
      mapOptionsButton: {
        '&:hover': {
          background: colors.white,
        },
      },
      icon: {
        marginRight: 12,
      },
      buttonText: {
        fontWeight: 600,
      },
      divider: {
        backgroundColor: colors.lightGray1,
        width: 1,
        height: 'auto',
        margin: '0 6px',
      },
    }),
);

export function OutagesPage({ path, children }: OutagesPageProps) {
  return (
    <>
      <Fragment>
        <OutageResponsivePageTrackerProvider>
          <OutageMapOptionsProvider>
            <OutageMapProvider>
              <OutageLeftPanelStateProvider>
                <OutageAccountListProvider>
                  <OutagesContent />
                </OutageAccountListProvider>
              </OutageLeftPanelStateProvider>
            </OutageMapProvider>
          </OutageMapOptionsProvider>
        </OutageResponsivePageTrackerProvider>
      </Fragment>
    </>
  );
}

function OutagesContent() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery('(max-width:400px)');
  const isMobile = useIsMobile();
  const { panelOpen } = useOutageLeftPanelState();
  const classes = useStyles({ isMobile, panelOpen });
  const { activeOutages } = useOutageEventsDetails();
  const { toggledOnCount } = useOutageMapOptions();
  const [openCountyModal, setOpenCountyModal] = useState(false);
  const [showMapOptions, setShowMapOptions] = useState(false);

  return (
    <>
      {isMobile && <MobileSubHeader />}
      <OutageCenterMap />
      <OutageLeftPanel />
      {!isMobile && (
        <Grid className={classes.headerStyles}>
          <OutageHeaderSearchBar isSearchVisible />
        </Grid>
      )}
      <div className={classes.buttonContainer}>
        <Button
          className={classes.mapOptionsButton}
          onClick={() => setOpenCountyModal(true)}
        >
          <AlertIcon className={classes.icon} />
          <Typography className={classes.buttonText}>
            {typeof activeOutages === 'number'
              ? activeOutages.toLocaleString()
              : activeOutages}{' '}
            {t('OUTAGES')}
          </Typography>
        </Button>
        <Divider orientation="vertical" className={classes.divider} />
        <Button
          className={classes.mapOptionsButton}
          onClick={() => setShowMapOptions(true)}
        >
          {toggledOnCount > 0 ? (
            <MapOptionsSelected className={classes.icon} />
          ) : (
            <MapOptions className={classes.icon} />
          )}
          <Typography
            className={classes.buttonText}
            style={{ color: toggledOnCount > 0 ? 'black' : undefined }}
          >
            {!isSmallScreen && <span>{t('MAP_OPTIONS')}</span>}
            {toggledOnCount > 0
              ? isSmallScreen
                ? toggledOnCount
                : ` (${toggledOnCount})`
              : ''}
          </Typography>
        </Button>
      </div>

      {openCountyModal && (
        <ReportOutageByCountyCity
          open={openCountyModal}
          setOpen={setOpenCountyModal}
        />
      )}
      {showMapOptions && (
        <OutageMapDialog
          open={showMapOptions}
          setOpen={setShowMapOptions}
          setValue={1}
        />
      )}
      <MultiPremisePopup />
    </>
  );
}
